import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { ApiService } from '@services/api.service';
import { DevCycleService } from '@services/dev-cycle.service';
import { MatDialogService } from '@services/dialogs.service';
import { FormatCurrencyService } from '@services/format-currency.service';
import {
  DOCUMENTS_MASK,
  DocumentType,
  UPLOAD_STATUS,
} from '@public/make-payment/payment-request/payment-request-v1.component';
import {
  ButtonTextByStep,
  MatStepperCustomComponent,
} from '@shared/mat-stepper-custom/mat-stepper-custom.component';
import { EntryKeys } from '@shared/upload-documents/upload-documents.component';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import {
  catchError,
  map,
  of,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import {
  MAX_SPREAD,
  MIN_SPREAD,
} from '../custom-spread-dialog/custom-spread-dialog.types';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';

type CurrencyAccounts = {
  balance: number;
  company: {
    companyId: string;
    country: string;
    name: string;
  };
  companyCurrencyAccountId: string;
  currencyIso: string;
  incomingBalance: number;
  lastTransaction: null;
  createdAt: Date;
  updatedAt: Date;
};

type ApiResponse<T> = {
  success: boolean;
  records: T;
  record: T;
};

type PaymentRequest = {
  paymentRequestId: string;
  rawId?: string;
  order?: {
    providerName: string;
    orderId: string;
  };
  payment?: {
    amount: number;
    currency: string;
  };
  student?: {
    firstname: string;
    lastname: string;
    country?: string;
  };
  paymentLinkUrl: string;
};

@Component({
  selector: 'ew-student-payments',
  templateUrl: './student-payments.component.html',
  styleUrl: './student-payments.component.scss',
})
export class StudentPaymentsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  startDate = new Date(1992, 0, 1);
  hidePayerDetails: boolean = true;
  hideStudentDetails: boolean = true;
  futureBalance!: number;
  loading: boolean = false;
  showPaymentRequestFeatureFlag: boolean = false;
  currencyAccounts: CurrencyAccounts[] = [];
  paymentLinkBaseUrl: string = `${window.location.origin}/public/payment-request/`;
  paymentRequest: PaymentRequest | null = null;
  tooltipMessage: string = 'bankTransfer:tooltipMessage';
  errors: any;

  // Spread form related properties
  showSpreadForm: boolean = false;
  minSpread = MIN_SPREAD;
  maxSpread = MAX_SPREAD;
  defaultSpread: number | null = null;
  spreadForm: FormGroup = new FormGroup({
    spread: new FormControl(null, [
      Validators.required,
      Validators.min(MIN_SPREAD),
      Validators.max(MAX_SPREAD),
      Validators.pattern(/^\d+(\.\d{1,2})?$/),
    ]),
  });
  nextButtonLabels: ButtonTextByStep = [
    'studentPaymentV2:continueToInvoice',
    'studentPaymentV2:continueToStudent',
    'studentPaymentV2:continueToPayment',
    'studentPaymentV2:generatePaymentLink',
  ];
  documentLabel = '';
  documentMask: string | null = null;
  documentDefault = '';
  documentExample = '';
  documentSubscription: Subscription | undefined;
  isDocumentRequiredForPayer: boolean = false;

  @ViewChild('stepper') stepper!: MatStepperCustomComponent;

  invoiceToUpload: DocumentType[] = [
    {
      label: 'uploadDocuments:invoice',
      key: 'invoice',
      status: UPLOAD_STATUS.Pending,
    },
  ];
  uploadedInvoiceId: EntryKeys = {};
  // Temporary storage for invoice file (Brazil only)
  temporaryInvoiceFile: File | null = null;
  // Temporary storage for passport files (all passport-required countries)
  temporaryStudentPassportFile: File | null = null;
  temporaryPayerPassportFile: File | null = null;

  passportToUpload: DocumentType[] = [
    {
      label: 'uploadDocuments:passportOrId',
      key: 'nationalId',
      status: UPLOAD_STATUS.Pending,
    },
  ];
  uploadedPassportId: EntryKeys = {};

  payerPassportToUpload: DocumentType[] = [
    {
      label: 'uploadDocuments:passportOrId',
      key: 'nationalId',
      status: UPLOAD_STATUS.Pending,
    },
  ];
  payerUploadedPassportId: EntryKeys = {};

  payer: FormGroup = new FormGroup({
    country: new FormControl('', [Validators.required]),
    language: new FormControl(localStorage.getItem('lang'), [
      Validators.required,
    ]),
    document: new FormControl(''),
    nationalId: new FormControl(''),
    whoIsMakingThePayment: new FormControl(null),
    passport: new FormControl(''),
    firstname: new FormControl(''),
    lastname: new FormControl(''),
    city: new FormControl('', [Validators.maxLength(50)]),
    address: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(255),
    ]),
    postalCode: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(10),
    ]),
    email: new FormControl('', [Validators.email, Validators.maxLength(50)]),
    phoneNumber: new FormControl('', [
      Validators.minLength(10),
      Validators.maxLength(20),
      Validators.pattern('^[0-9\\(\\)\\-\\+\\s]*$'),
    ]),
  });

  order: FormGroup = new FormGroup({
    orderId: new FormControl('', [Validators.required]),
    providerName: new FormControl('', [Validators.required]),
    invoice: new FormControl(''),
  });

  student: FormGroup = new FormGroup({
    firstname: new FormControl('', [
      Validators.required,
      Validators.maxLength(120),
    ]),
    lastname: new FormControl('', [
      Validators.required,
      Validators.maxLength(120),
    ]),
    country: new FormControl(''),
    externalId: new FormControl(''),
    email: new FormControl('', [Validators.email]),
    mobile: new FormControl('', [
      Validators.minLength(10),
      Validators.maxLength(20),
      Validators.pattern('^[0-9\\(\\)\\-\\+\\s]*$'),
    ]),
    city: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(50),
    ]),
    address: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(255),
    ]),
    zipCode: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(10),
    ]),
    dateOfBirth: new FormControl(''),
    passport: new FormControl(''),
  });

  payment: FormGroup = new FormGroup({
    currencyAccount: new FormControl(null, [Validators.required]),
    amount: new FormControl(1, [
      Validators.required,
      Validators.min(0.01),
      Validators.max(5_000_000),
    ]),
  });

  constructor(
    private readonly API: ApiService,
    private readonly formatCurrency: FormatCurrencyService,
    private readonly translate: TranslateService,
    private devCycleService: DevCycleService,
    public matDialogService: MatDialogService,
    private router: Router,
    private dialogRef: MatDialogRef<StudentPaymentsComponent>,
  ) {}

  ngOnInit(): void {
    this.getDocumentValidations();
    this.getCurrencyAccounts();

    this.payer.controls['country'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.student.controls['country'].setValue(value);
      });

    this.payment.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map((value) => ({
          currencyAccount: value.currencyAccount,
          amount:
            typeof value.amount === 'string'
              ? Number(value.amount)
              : value.amount,
        })),
      )
      .subscribe(({ currencyAccount, amount }) => {
        if (currencyAccount) {
          this.futureBalance = Number(currencyAccount.balance);

          // Set the spread value if available
          if (
            currencyAccount.spread !== undefined &&
            currencyAccount.spread !== null
          ) {
            this.defaultSpread = currencyAccount.spread;
            this.spreadForm.get('spread')?.setValue(currencyAccount.spread);
          }
        }

        if (amount) {
          this.updateFutureBalance(amount);
        }
      });

    this.payer.get('country')?.valueChanges.subscribe(() => {
      this.updateInvoiceValidation();
    });

    this.setPaymentRequestFeatureFlag();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get validations() {
    const paymentStepValid =
      this.payment.valid && (!this.showSpreadForm || this.spreadForm.valid);

    return [
      this.payer.valid,
      this.isOrderStepValid(),
      this.student.valid,
      paymentStepValid,
      true,
    ];
  }

  isOrderStepValid(): boolean {
    // For Brazil, check if temporary file is stored instead of relying only on form validation
    if (this.payer.get('country')?.value === 'br') {
      const requiredFieldsValid =
        (this.order.get('orderId')?.valid ?? false) &&
        (this.order.get('providerName')?.valid ?? false);
      const invoiceValid =
        !!this.temporaryInvoiceFile ||
        (this.order.get('invoice')?.valid ?? false);
      return requiredFieldsValid && invoiceValid;
    }
    return this.order.valid;
  }

  getCurrencyAccounts(): void {
    this.loading = true;

    this.API.get('v1/company-currency-accounts/').subscribe(
      (data: ApiResponse<CurrencyAccounts[]>) => {
        this.currencyAccounts = data.records;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
      },
    );
  }

  getCurrencySymbol(currency: string) {
    return this.formatCurrency.getCurrencySymbol(currency);
  }

  updateFutureBalance(amount: number) {
    const balance = this.payment.get('currencyAccount')?.value?.balance;
    if (balance) {
      this.futureBalance = Number(balance) + Number(amount);
      return;
    }

    this.futureBalance = Number(amount);
  }

  updateInvoiceValidation() {
    if (this.isInvoiceRequired()) {
      this.order.controls['invoice'].setValidators([Validators.required]);
    } else {
      this.order.controls['invoice'].clearValidators();
    }
    this.order.controls['invoice'].updateValueAndValidity();
  }

  isInvoiceRequired(): boolean {
    const selectedCountry = this.payer.get('country')?.value;
    const requiredCountries = ['br'];
    return requiredCountries.includes(selectedCountry);
  }

  handleInvoiceUpload(): void {
    const invoice = this.uploadedInvoiceId.invoice;
    this.order.patchValue({ invoice });
  }

  /**
   * Handle invoice file selection for Brazil - store file temporarily instead of uploading immediately
   */
  handleInvoiceFileSelection(event: { file: File; key: string }): void {
    if (this.payer.get('country')?.value === 'br' && event.key === 'invoice') {
      this.temporaryInvoiceFile = event.file;
      // Mark as uploaded in UI to allow form progression
      this.invoiceToUpload[0].status = UPLOAD_STATUS.Uploaded;
      this.order.patchValue({ invoice: 'temporary-invoice-file' });
      // Force validation update to enable the Continue button
      this.order.controls['invoice'].updateValueAndValidity();
      // Also trigger the handleInvoiceUpload to ensure consistency
      this.handleInvoiceUpload();
    }
  }

  /**
   * Upload invoice file to the new v2/payment-requests/:uuid/files endpoint
   */
  private async uploadInvoiceFile(paymentRequestUuid: string): Promise<void> {
    if (!this.temporaryInvoiceFile) {
      return;
    }

    const formData = new FormData();
    formData.append(
      'file',
      this.temporaryInvoiceFile,
      this.temporaryInvoiceFile.name,
    );
    formData.append('fileType', 'INVOICE');

    return new Promise((resolve, reject) => {
      this.API.post(
        `v2/payment-requests/${paymentRequestUuid}/files`,
        formData,
      ).subscribe({
        next: (response: any) => {
          // Clear temporary file after successful upload
          this.temporaryInvoiceFile = null;
          resolve();
        },
        error: (error) => {
          console.error('Error uploading invoice file:', error);
          reject(error);
        },
      });
    });
  }

  /**
   * Upload passport file to the new v2/payment-requests/:uuid/files endpoint
   */
  private async uploadPassportFile(
    paymentRequestUuid: string,
    file: File,
    fileType: 'STUDENT_PASSPORT' | 'PAYER_PASSPORT',
  ): Promise<void> {
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('fileType', 'PASSPORT');

    return new Promise((resolve, reject) => {
      this.API.post(
        `v2/payment-requests/${paymentRequestUuid}/files`,
        formData,
      ).subscribe({
        next: (response: any) => {
          resolve();
        },
        error: (error) => {
          console.error(`Error uploading ${fileType} file:`, error);
          reject(error);
        },
      });
    });
  }

  isPassportRequiredForPayer(): boolean {
    const whoIsMakingThePayment = this.payer.get(
      'whoIsMakingThePayment',
    )?.value;
    return (
      this.isPassportRequired() &&
      ['MOTHER', 'FATHER'].includes(whoIsMakingThePayment)
    );
  }

  isPassportRequiredForStudent(): boolean {
    const whoIsMakingThePayment = this.payer.get(
      'whoIsMakingThePayment',
    )?.value;
    return this.isPassportRequired() && whoIsMakingThePayment === 'STUDENT';
  }

  isPassportRequired(): boolean {
    const selectedCountry = this.student.get('country')?.value;
    // We must confirm the values below. I found something about from the Niagara University
    // and Jacksonville State University, but this is not conclusive:
    // https://www.niagara.edu/wp-content/uploads/2023/12/TransferMate-Payments-Student-FAQ-V2.pdf
    // https://www.jsu.edu/bursar/transfermate-faq.html
    const requiredCountries = ['br', 'cn', 'in', 'za'];
    return requiredCountries.includes(selectedCountry);
  }

  handlePassportUpload(): void {
    const passport = this.uploadedPassportId.nationalId;
    this.student.patchValue({ passport });
  }

  /**
   * Handle student passport file selection - store file temporarily instead of uploading immediately
   */
  handleStudentPassportFileSelection(event: { file: File; key: string }): void {
    if (this.isPassportRequired() && event.key === 'nationalId') {
      this.temporaryStudentPassportFile = event.file;
      // Mark as uploaded in UI to allow form progression
      this.passportToUpload[0].status = UPLOAD_STATUS.Uploaded;
      this.student.patchValue({ passport: 'temporary-passport-file' });
      // Force validation update to enable the Continue button
      this.student.controls['passport'].updateValueAndValidity();
      // Also trigger the handlePassportUpload to ensure consistency
      this.handlePassportUpload();
    }
  }

  handlePayerPassportUpload(): void {
    const passport = this.payerUploadedPassportId.nationalId;
    this.payer.patchValue({ passport });
  }

  /**
   * Handle payer passport file selection - store file temporarily instead of uploading immediately
   */
  handlePayerPassportFileSelection(event: { file: File; key: string }): void {
    if (this.isPassportRequired() && event.key === 'nationalId') {
      this.temporaryPayerPassportFile = event.file;
      // Mark as uploaded in UI to allow form progression
      this.payerPassportToUpload[0].status = UPLOAD_STATUS.Uploaded;
      this.payer.patchValue({ passport: 'temporary-passport-file' });
      // Force validation update to enable the Continue button
      this.payer.controls['passport'].updateValueAndValidity();
      // Also trigger the handlePayerPassportUpload to ensure consistency
      this.handlePayerPassportUpload();
    }
  }

  onCountryChange($event: { iso: string }) {
    this.student.patchValue({ country: $event.iso });
    this.order.controls['invoice'].updateValueAndValidity();
  }

  submit() {
    this.loading = true;
    const formData = this.prepareFormData();

    if (!formData) {
      this.loading = false;
      return;
    }
    this.API.post('v2/payment-requests/', formData)
      .pipe(
        catchError((error) => {
          this.handleSubmitError(error);
          this.stepper.previous();
          return of(null);
        }),
      )
      .subscribe({
        next: async (data) => {
          if (data?.records) {
            this.paymentRequest = {
              paymentRequestId: `S${data.records.paymentRequestId}`,
              rawId: data.records.paymentRequestId,
              order: {
                providerName: data.records.order.providerName,
                orderId: data.records.order.orderId,
              },
              payment: {
                amount: data.records.payment.amount,
                currency: data.records.payment.currency,
              },
              student: {
                firstname: this.student.value.firstname,
                lastname: this.student.value.lastname,
                country: this.student.value.country,
              },
              paymentLinkUrl: data.records.paymentLinkUrl,
            };
            this.paymentLinkBaseUrl = data.records.paymentLinkUrl;

            // Upload invoice file for Brazil after payment request creation
            if (
              this.temporaryInvoiceFile &&
              this.payer.get('country')?.value === 'br'
            ) {
              try {
                const paymentRequestUuid =
                  data.records.uuid || data.records.payment_request_uuid;
                await this.uploadInvoiceFile(paymentRequestUuid);
              } catch (error) {
                console.error('Failed to upload invoice file:', error);
                // Continue with the flow even if invoice upload fails
              }
            }

            // Upload passport files for all passport-required countries after payment request creation
            const paymentRequestUuid =
              data.records.uuid || data.records.payment_request_uuid;

            // Upload student passport if required and file exists
            if (
              this.temporaryStudentPassportFile &&
              this.isPassportRequiredForStudent()
            ) {
              try {
                await this.uploadPassportFile(
                  paymentRequestUuid,
                  this.temporaryStudentPassportFile,
                  'STUDENT_PASSPORT',
                );
                this.temporaryStudentPassportFile = null;
              } catch (error) {
                console.error('Failed to upload student passport file:', error);
              }
            }

            // Upload payer passport if required and file exists
            if (
              this.temporaryPayerPassportFile &&
              this.isPassportRequiredForPayer()
            ) {
              try {
                await this.uploadPassportFile(
                  paymentRequestUuid,
                  this.temporaryPayerPassportFile,
                  'PAYER_PASSPORT',
                );
                this.temporaryPayerPassportFile = null;
              } catch (error) {
                console.error('Failed to upload payer passport file:', error);
              }
            }
          }
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  openPaymentLink(url: string): void {
    window.open(url, '_blank');
  }

  /**
   * Toggles the spread form visibility
   */
  toggleSpreadForm(): void {
    this.showSpreadForm = !this.showSpreadForm;

    // Initialize the spread form with the current spread value
    if (this.showSpreadForm) {
      const currentSpread = this.payment.get('currencyAccount')?.value?.spread;
      if (currentSpread !== undefined && currentSpread !== null) {
        this.spreadForm.get('spread')?.setValue(currentSpread);
      }
    } else {
      if (this.defaultSpread !== null) {
        this.spreadForm.get('spread')?.setValue(this.defaultSpread);
      }
    }
  }

  private prepareFormData(): any {
    try {
      let formData: any = {
        paymentLinkBaseUrl: this.paymentLinkBaseUrl,
        language: this.payer.value.language,
        sellerPlatform: 'EDWALLET',
        student: {
          ...this.student.value,
          dateOfBirth: this.student.value.dateOfBirth
            ? moment(this.student.value.dateOfBirth).format('YYYY-MM-DD')
            : null,
          platformId: this.student.value.externalId,
          phoneNumber: this.student.value.mobile,
          postalCode: this.student.value.zipCode,
        },
        order:
          this.payer.get('country')?.value === 'br'
            ? { ...this.order.value, invoice: undefined }
            : this.order.value,
        payment: {
          ...this.payment.value,
          currencyAccountId:
            this.payment.value.currencyAccount.companyCurrencyAccountId,
        },
        payer: this.payer.value,
      };

      // Add customSpread if it exists and form is valid
      if (this.showSpreadForm && this.spreadForm.valid) {
        const spreadValue = this.spreadForm.get('spread')?.value;
        if (spreadValue !== null && spreadValue !== undefined) {
          formData.customSpread = parseFloat(spreadValue);
        }
      }

      return formData;
    } catch (error) {
      this.API.alertService.error('errors.invalidFormData');
      return null;
    }
  }

  private handleSubmitError(error: HttpErrorResponse) {
    if (error.status >= 500) {
      if (error?.error?.errors && Array.isArray(error.error.errors)) {
        this.errors = error.error.errors.map((e: any) => {
          if (e.details) {
            return e.details.originalMessage;
          }

          return e.message;
        });
      }
    } else if (error.status < 500 && error.error) {
      this.errors = [error.error.message];
    }
  }

  public copyToClipboard(value: string | null, tooltip: MatTooltip): void {
    if (value) {
      navigator.clipboard.writeText(value).then(() => {
        this.tooltipMessage = this.translate.instant(
          'bankTransfer:copyMessage',
        );

        tooltip.show();

        setTimeout(() => {
          this.tooltipMessage = 'bankTransfer:tooltipMessage';
          tooltip.hide();
        }, 2000);
      });
    }
  }

  private async setPaymentRequestFeatureFlag() {
    this.showPaymentRequestFeatureFlag = await this.devCycleService.getVariable(
      'ew-2016-revisit-student-payment',
      false,
    );
  }

  onPayerCountryChange() {
    if (this.payer.value.country) {
      this.setDocumentMask(this.payer.value.country);
      this.getDocumentValidations();
    }
  }

  setDocumentMask(countryPayFrom: string): void {
    const countryMask: string = (DOCUMENTS_MASK as any)[
      countryPayFrom.replace(
        /\w\S*/g,
        (text) => text.charAt(0).toUpperCase() + text.substring(1),
      )
    ];

    this.documentMask = countryMask || '';
  }

  getDocumentValidations(): void {
    this.translate
      .get('createRequestMoney:payerInputDocument')
      .subscribe((value) => {
        this.documentDefault = value;
        this.documentLabel = this.documentDefault;
      });

    this.API.get(`v1/public/country/${this.payer.value.country}`).subscribe(
      (res: any) => {
        const document = res?.record?.document;
        this.isDocumentRequiredForPayer = !!document?.requiredForPayer;
        if (res.success && document) {
          this.documentLabel = `${this.documentDefault}: ${document.name}`;
          this.documentExample = document.example;
          this.payer
            .get('document')
            ?.setValidators([Validators.pattern(document.format)]);
        } else {
          this.documentExample = '';
          this.payer.get('document')?.setValue(undefined);
          this.payer.get('document')?.setValidators([]);
        }
      },
    );

    this.payer.get('document')?.updateValueAndValidity();
  }

  redirectToPaymentDetails(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(
      `/student-payments/detail/${this.paymentRequest?.rawId || this.paymentRequest?.paymentRequestId?.replace('S', '')}`,
    );
  }
}
